/**
 * Tutorial Service for managing tutorials and user progress
 * Handles API calls, caching, and local state management
 */

import api from './api';
import logger from '../utils/logger';
import tokenManager from '../utils/tokenManager';

class TutorialService {
  constructor() {
    this.pendingTutorials = new Map(); // Only tutorials user needs
    this.userProgress = new Map(); // User progress cache
    this.changedProgress = new Map(); // Track which tutorials have changed since last sync
    this.lastFetch = 0;
    this.cacheTimeout = 300000; // 5 minutes
    this.cacheKey = 'pending_tutorials_cache';
  }

  /**
   * Load pending tutorials + progress in a single API call.
   * Use this at startup instead of calling loadPendingTutorials() + loadUserProgress() separately.
   */
  async loadStartupData() {
    const token = tokenManager.getToken();
    if (!token) {
      return { pending: this.loadFromCache(), progress: [] };
    }

    try {
      const response = await api.get('/tutorials/me/startup');
      const { pending, progress } = response.data;

      this.pendingTutorials.clear();
      pending.forEach(t => this.pendingTutorials.set(t.tutorial_key, t));
      this.cacheInLocalStorage(pending, Date.now());
      this.lastFetch = Date.now();

      progress.forEach(p => this.userProgress.set(p.tutorial_key, p));

      return { pending, progress };
    } catch (error) {
      logger.error('TutorialService: Failed to load startup data:', error);
      return { pending: this.loadFromCache(), progress: [] };
    }
  }

  /**
   * Load only pending tutorials for the user
   * Single API call that returns only tutorials the user needs to complete
   */
  async loadPendingTutorials() {
    const now = Date.now();
    
    // Check cache first
    if (this.pendingTutorials.size > 0 && (now - this.lastFetch) < this.cacheTimeout) {
      return Array.from(this.pendingTutorials.values());
    }

    try {
      // Check if user is authenticated before making API call
      const token = tokenManager.getToken();
      if (!token) {
        return this.loadFromCache();
      }

      const response = await api.get('/tutorials/me/pending');
      const tutorials = response.data;
      
      // Cache the results
      this.pendingTutorials.clear();
      tutorials.forEach(tutorial => {
        this.pendingTutorials.set(tutorial.tutorial_key, tutorial);
      });
      
      this.cacheInLocalStorage(tutorials, now);
      
      this.lastFetch = now;
      return tutorials;
    } catch (error) {
      logger.error('TutorialService: Failed to load pending tutorials:', error);
      // Fallback to localStorage cache
      return this.loadFromCache();
    }
  }

  /**
   * Get specific tutorial from cache
   */
  getTutorial(key) {
    return this.pendingTutorials.get(key);
  }

  /**
   * Find tutorial key for a given route pattern
   * Uses route_pattern from database instead of hardcoded mappings
   */
  findTutorialForRoute(pathname) {
    const matchPath = require('react-router-dom').matchPath;

    // Count literal (non-param) segments — more literals = more specific
    const specificity = (pattern) =>
      pattern.split('/').filter(seg => seg && !seg.startsWith(':')).length;

    let bestKey = null;
    let bestScore = -1;

    for (const tutorial of this.pendingTutorials.values()) {
      if (tutorial.route_pattern && matchPath(tutorial.route_pattern, pathname)) {
        const score = specificity(tutorial.route_pattern);
        if (score > bestScore) {
          bestScore = score;
          bestKey = tutorial.tutorial_key;
        }
      }
    }

    return bestKey;
  }

  /**
   * Check if user has tutorial for specific route
   */
  hasTutorialForRoute(pathname) {
    const tutorialKey = this.findTutorialForRoute(pathname);
    return tutorialKey !== null;
  }

  /**
   * Check if tutorial should be shown (no API call)
   */
  shouldShowTutorial(key, userContext = {}) {
    const progress = this.userProgress.get(key);
    if (progress && progress.status === 'completed') {
      return false;
    }

    const tutorial = this.getTutorial(key);
    if (!tutorial || !tutorial.is_active) {
      return false;
    }

    // Check prerequisites
    if (tutorial.prerequisites && tutorial.prerequisites.length > 0) {
      const allPrereqsMet = tutorial.prerequisites.every(prereqKey => {
        const prereqProgress = this.userProgress.get(prereqKey);
        return prereqProgress && prereqProgress.status === 'completed';
      });
      
      if (!allPrereqsMet) {
        return false;
      }
    }

    return true;
  }

  /**
   * Update tutorial progress (batch API call)
   */
  async updateProgress(tutorialKey, status, currentStep = null) {
    if (!tutorialKey) {
      logger.warn('TutorialService: updateProgress called with empty tutorialKey — ignoring');
      return;
    }
    // Update local cache immediately
    const existing = this.userProgress.get(tutorialKey) || {
      tutorial_key: tutorialKey,
      status: 'pending',
      current_step: 0
    };

    existing.status = status;
    if (currentStep !== null) {
      existing.current_step = currentStep;
    }
    if (status === 'completed' && !existing.completed_at) {
      existing.completed_at = new Date().toISOString();
    }

    this.userProgress.set(tutorialKey, existing);
    
    // Track this tutorial as changed
    this.changedProgress.set(tutorialKey, existing);

    // Remove from pending if completed
    if (status === 'completed') {
      this.pendingTutorials.delete(tutorialKey);
    }

    // Batch API call (debounced)
    this.debouncedProgressUpdate();
  }

  /**
   * Load user progress once per session
   */
  async loadUserProgress() {
    try {
      // Check if user is authenticated before making API call
      const token = tokenManager.getToken();
      if (!token) {
        return [];
      }

      const response = await api.get('/tutorials/me/progress');
      const progress = response.data;
      
      // Cache progress
      progress.forEach(p => {
        this.userProgress.set(p.tutorial_key, p);
      });
      
      return progress;
    } catch (error) {
      logger.error('TutorialService: Failed to load tutorial progress:', error);
      return [];
    }
  }

  /**
   * Check if any tutorials are pending
   */
  hasPendingTutorials() {
    return this.pendingTutorials.size > 0;
  }

  /**
   * Get all pending tutorials
   */
  getPendingTutorials() {
    return Array.from(this.pendingTutorials.values());
  }

  /**
   * Reset all tutorial progress
   */
  async resetProgress(tutorialKey = null) {
    try {
      // Check if user is authenticated before making API call
      const token = tokenManager.getToken();
      if (!token) {
        logger.debug('TutorialService: No authentication token, skipping progress reset');
        // Still clear local state even if API call fails
        if (tutorialKey) {
          this.userProgress.delete(tutorialKey);
          this.pendingTutorials.delete(tutorialKey);
        } else {
          this.userProgress.clear();
          this.pendingTutorials.clear();
        }
        return;
      }

      await api.delete('/tutorials/me/progress', {
        params: tutorialKey ? { tutorial_key: tutorialKey } : {}
      });
      
      if (tutorialKey) {
        this.userProgress.delete(tutorialKey);
        // Add back to pending if it exists in definitions
        this.pendingTutorials.delete(tutorialKey);
      } else {
        this.userProgress.clear();
        this.pendingTutorials.clear();
      }
      
      logger.debug(`TutorialService: Reset progress for ${tutorialKey || 'all tutorials'}`);
    } catch (error) {
      logger.error('TutorialService: Failed to reset progress:', error);
      // Still clear local state on error
      if (tutorialKey) {
        this.userProgress.delete(tutorialKey);
        this.pendingTutorials.delete(tutorialKey);
      } else {
        this.userProgress.clear();
        this.pendingTutorials.clear();
      }
    }
  }

  /**
   * Cache tutorials in localStorage
   */
  cacheInLocalStorage(tutorials, timestamp) {
    try {
      const cacheData = {
        data: tutorials,
        timestamp: timestamp,
        version: '1.0'
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      logger.warn('TutorialService: Failed to cache in localStorage:', error);
    }
  }

  /**
   * Load tutorials from localStorage cache
   */
  loadFromCache() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < this.cacheTimeout) {
          logger.debug('TutorialService: Using localStorage cache');
          this.pendingTutorials.clear();
          data.forEach(tutorial => {
            this.pendingTutorials.set(tutorial.tutorial_key, tutorial);
          });
          return data;
        }
      }
    } catch (error) {
      logger.warn('TutorialService: Failed to load from cache:', error);
    }
    return [];
  }

  /**
   * Debounced progress updates to minimize API calls
   */
  debouncedProgressUpdate = this.debounce(async () => {
    try {
      // Check if user is authenticated before making API call
      const token = tokenManager.getToken();
      if (!token) {
        logger.debug('TutorialService: No authentication token, skipping progress update');
        return;
      }

      // Purge any entries with a falsy key — these can accumulate when a
      // stale Driver.js callback fires after the tutorial has been cleared,
      // causing undefined to become a map key.  Removing them here ensures
      // a stuck bad entry never blocks future syncs even after a 422.
      for (const key of this.changedProgress.keys()) {
        if (!key) {
          logger.warn(`TutorialService: Removing invalid changedProgress entry (key=${key})`);
          this.changedProgress.delete(key);
        }
      }

      // Only send tutorials that have changed since last sync.
      // Use entries() so tutorial_key is always guaranteed from the map key,
      // regardless of whether the stored object already has the field.
      const progressArray = Array.from(this.changedProgress.entries()).map(
        ([key, value]) => ({ ...value, tutorial_key: key })
      );
      if (progressArray.length === 0) return;
      
      logger.debug(`TutorialService: Batch updating ${progressArray.length} changed tutorial(s)`);
      await api.put('/tutorials/me/progress', {
        progress: progressArray
      });
      
      // Clear changed progress after successful sync
      this.changedProgress.clear();
    } catch (error) {
      logger.error('TutorialService: Failed to update progress:', error);
      // Don't clear changedProgress on error so it can retry
    }
  }, 1000);

  /**
   * Simple debounce implementation
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.pendingTutorials.clear();
    this.userProgress.clear();
    this.changedProgress.clear();
    this.lastFetch = 0;
    localStorage.removeItem(this.cacheKey);
  }
}

// Create singleton instance
const tutorialService = new TutorialService();

export default tutorialService;
